import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import './styles.css';
import { AuthPage } from './features/auth/AuthPage';
import { RootRedirect } from './features/shell/RootRedirect';
import { NotFoundPage } from './features/shell/NotFoundPage';
import { RouteError } from './features/shell/RouteError';
import { takePendingReload } from './lib/sw';

/*
 * Route-level code splitting.
 *
 * Every one of these pages used to be imported eagerly into a single 1.59 MB
 * bundle (462 KB gzipped), so a grants officer opening the dashboard on a
 * phone downloaded the grantmaker suite, the fundraising module, the report
 * builder and the demo workspace before anything rendered. On a community
 * office connection that is several seconds of blank screen for code the
 * session will never execute.
 *
 * Only what the first paint needs stays eager: the redirect that decides
 * where you land, the sign-in page most sessions start on, and the 404.
 * The shell is lazy too, because it reads Firestore: eager, it put the
 * database SDK in front of the sign-in form (see src/lib/firebaseApp.ts).
 * The sign-in page and the root redirect fetch it ahead of need.
 */
let shellLoaded = false;
const AppShell = lazy(() =>
  import('./features/shell/AppShell').then((m) => {
    shellLoaded = true;
    return { default: m.AppShell };
  }),
);
const OnboardingPage = lazy(() => import('./features/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage })));
const CalendarPage = lazy(() => import('./features/calendar/CalendarPage').then((m) => ({ default: m.CalendarPage })));
const CompliancePage = lazy(() => import('./features/compliance/CompliancePage').then((m) => ({ default: m.CompliancePage })));
const PartiesPage = lazy(() => import('./features/parties/PartiesPage').then((m) => ({ default: m.PartiesPage })));
const TasksPage = lazy(() => import('./features/tasks/TasksPage').then((m) => ({ default: m.TasksPage })));
const AnswersPage = lazy(() => import('./features/answers/AnswersPage').then((m) => ({ default: m.AnswersPage })));
const DocumentsPage = lazy(() => import('./features/documents/DocumentsPage').then((m) => ({ default: m.DocumentsPage })));
const GrantDetailPage = lazy(() => import('./features/grants/GrantDetailPage').then((m) => ({ default: m.GrantDetailPage })));
const GrantsPage = lazy(() => import('./features/grants/GrantsPage').then((m) => ({ default: m.GrantsPage })));
const PipelinePage = lazy(() => import('./features/grants/GrantsPage').then((m) => ({ default: m.PipelinePage })));
const DemoPage = lazy(() => import('./features/demo/DemoPage').then((m) => ({ default: m.DemoPage })));
const CallsPage = lazy(() => import('./features/calls/CallsPage').then((m) => ({ default: m.CallsPage })));
const CallDetailPage = lazy(() => import('./features/calls/CallDetailPage').then((m) => ({ default: m.CallDetailPage })));
const BoardPage = lazy(() => import('./features/board/BoardPage').then((m) => ({ default: m.BoardPage })));
const DisbursementsPage = lazy(() => import('./features/disbursements/DisbursementsPage').then((m) => ({ default: m.DisbursementsPage })));
const EvidencePage = lazy(() => import('./features/evidence/EvidencePage').then((m) => ({ default: m.EvidencePage })));
const CasesPage = lazy(() => import('./features/cases/CasesPage').then((m) => ({ default: m.CasesPage })));
const GovernancePage = lazy(() => import('./features/governance/GovernancePage').then((m) => ({ default: m.GovernancePage })));
const BoardPackPage = lazy(() => import('./features/reports/BoardPackPage').then((m) => ({ default: m.BoardPackPage })));
const ReportBuilderPage = lazy(() => import('./features/reports/ReportBuilderPage').then((m) => ({ default: m.ReportBuilderPage })));
const MyWeekPage = lazy(() => import('./features/myweek/MyWeekPage').then((m) => ({ default: m.MyWeekPage })));
const FundabilityPage = lazy(() => import('./features/fundability/FundabilityPage').then((m) => ({ default: m.FundabilityPage })));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const InvitePage = lazy(() => import('./features/settings/InvitePage').then((m) => ({ default: m.InvitePage })));
const HomeGate = lazy(() => import('./features/onboarding/HomeGate').then((m) => ({ default: m.HomeGate })));
const WelcomePage = lazy(() => import('./features/onboarding/WelcomePage').then((m) => ({ default: m.WelcomePage })));
const ImportPage = lazy(() => import('./features/pipeline/ImportPage').then((m) => ({ default: m.ImportPage })));
const FunderDirectoryPage = lazy(() => import('./features/parties/FunderDirectoryPage').then((m) => ({ default: m.FunderDirectoryPage })));
const FunderPackPage = lazy(() => import('./features/reports/FunderPackPage').then((m) => ({ default: m.FunderPackPage })));
const SedLetterPage = lazy(() => import('./features/reports/SedLetterPage').then((m) => ({ default: m.SedLetterPage })));
const SedRegisterPage = lazy(() => import('./features/reports/SedRegisterPage').then((m) => ({ default: m.SedRegisterPage })));
const FundraisingPage = lazy(() => import('./features/fundraising/FundraisingPage').then((m) => ({ default: m.FundraisingPage })));
const CashFlowPage = lazy(() => import('./features/finance/CashFlowPage').then((m) => ({ default: m.CashFlowPage })));
const FundsPage = lazy(() => import('./features/finance/FundsPage').then((m) => ({ default: m.FundsPage })));
const AddonsPage = lazy(() => import('./features/settings/AddonsPage').then((m) => ({ default: m.AddonsPage })));
// Not lazy: it is small, it is the thing rendered INSTEAD of a lazy route,
// and a second suspense boundary inside the first is a blank screen twice.
import { AccountingNotYet, RequireAddon, RequireModule } from './features/shell/RequireModule';

const OutcomesPage = lazy(() => import('./features/outcomes/OutcomesPage').then((m) => ({ default: m.OutcomesPage })));

/*
 * The fallback while a route chunk arrives. Deliberately not a spinner: on a
 * fast connection it is never seen, and on a slow one a bare centred line
 * that matches the app's own type is calmer than a spinning ring. It is
 * announced, because a screen-reader user gets no visual cue that anything
 * is happening.
 */
function RouteFallback() {
  return (
    <div role="status" aria-live="polite" className="px-4 py-16 text-center text-sm text-ink-3">
      Loading…
    </div>
  );
}

/*
 * The workspace's error boundary. A page that throws renders its error in
 * the shell, so the header, the rail and the bottom bar stay. When the
 * shell's own chunk is what failed (an old tab after a deploy, or a phone
 * offline before the worker had cached it) there is no shell to draw it
 * in, and the lazy shell would only throw the same failure again, so the
 * error stands alone.
 */
function ShellError() {
  return shellLoaded ? <AppShell error /> : <RouteError />;
}

/*
 * Every route has an error boundary. There was none, so a route whose code
 * chunk was gone after a deploy (the normal state of an installed app's
 * tab) or that threw while rendering left a white screen with no way back.
 * In a workspace the shell renders the error itself, so the header, the
 * rail and the bottom bar stay.
 */
const router = createBrowserRouter([
  { path: '/', element: <RootRedirect />, errorElement: <RouteError /> },
  { path: '/demo', element: <Suspense fallback={<RouteFallback />}><DemoPage /></Suspense>, errorElement: <RouteError /> },
  { path: '/login', element: <AuthPage />, errorElement: <RouteError /> },
  // The address people type. It was a 404; it is the sign-up form.
  { path: '/signup', element: <AuthPage />, errorElement: <RouteError /> },
  { path: '/onboarding', element: <Suspense fallback={<RouteFallback />}><OnboardingPage /></Suspense>, errorElement: <RouteError /> },
  { path: '/invite/:orgId/:inviteId', element: <Suspense fallback={<RouteFallback />}><InvitePage /></Suspense>, errorElement: <RouteError /> },
  {
    path: '/o/:orgSlug',
    element: <Suspense fallback={<RouteFallback />}><AppShell /></Suspense>,
    errorElement: <ShellError />,
    children: [
      { index: true, element: <Suspense fallback={<RouteFallback />}><HomeGate /></Suspense> },
      { path: 'welcome', element: <Suspense fallback={<RouteFallback />}><WelcomePage /></Suspense> },
      { path: 'my-week', element: <Suspense fallback={<RouteFallback />}><MyWeekPage /></Suspense> },
      // Add-ons. Off, an add-on's address says what it does and how to
      // switch it on (RequireAddon), which also covers a module the plan
      // does not carry.
      { path: 'reports', element: <Suspense fallback={<RouteFallback />}><RequireAddon addon="reports"><ReportBuilderPage /></RequireAddon></Suspense> },
      { path: 'fundability', element: <Suspense fallback={<RouteFallback />}><RequireAddon addon="readiness"><FundabilityPage /></RequireAddon></Suspense> },
      { path: 'funder-pack', element: <Suspense fallback={<RouteFallback />}><RequireAddon addon="readiness"><FunderPackPage /></RequireAddon></Suspense> },
      { path: 'sed-letter', element: <Suspense fallback={<RouteFallback />}><RequireAddon addon="readiness"><SedLetterPage /></RequireAddon></Suspense> },
      { path: 'fundraising', element: <Suspense fallback={<RouteFallback />}><RequireAddon addon="donors"><FundraisingPage /></RequireAddon></Suspense> },
      { path: 'cash-flow', element: <Suspense fallback={<RouteFallback />}><RequireAddon addon="cashflow"><CashFlowPage /></RequireAddon></Suspense> },
      { path: 'funds', element: <Suspense fallback={<RouteFallback />}><RequireAddon addon="cashflow"><FundsPage /></RequireAddon></Suspense> },
      // Accounting is not offered for now. Its screen is still in
      // features/integrations; this address says it is not available yet.
      { path: 'integrations', element: <AccountingNotYet /> },
      { path: 'outcomes', element: <Suspense fallback={<RouteFallback />}><RequireAddon addon="outcomes"><OutcomesPage /></RequireAddon></Suspense> },
      { path: 'add-ons', element: <Suspense fallback={<RouteFallback />}><AddonsPage /></Suspense> },
      // seeker OR grantmaker: a fund's own outgoing awards are grants too.
      { path: 'grants', element: <Suspense fallback={<RouteFallback />}><RequireModule module={['seeker', 'grantmaker']}><GrantsPage /></RequireModule></Suspense> },
      { path: 'pipeline', element: <Suspense fallback={<RouteFallback />}><RequireAddon addon="pipeline"><PipelinePage /></RequireAddon></Suspense> },
      { path: 'import', element: <Suspense fallback={<RouteFallback />}><RequireModule module="seeker"><ImportPage /></RequireModule></Suspense> },
      { path: 'directory', element: <Suspense fallback={<RouteFallback />}><RequireModule module="seeker"><FunderDirectoryPage /></RequireModule></Suspense> },
      { path: 'grants/:grantId', element: <Suspense fallback={<RouteFallback />}><RequireModule module={['seeker', 'grantmaker']}><GrantDetailPage /></RequireModule></Suspense> },
      { path: 'calls', element: <Suspense fallback={<RouteFallback />}><RequireModule module="grantmaker"><CallsPage /></RequireModule></Suspense> },
      { path: 'calls/:callId', element: <Suspense fallback={<RouteFallback />}><RequireModule module="grantmaker"><CallDetailPage /></RequireModule></Suspense> },
      { path: 'board', element: <Suspense fallback={<RouteFallback />}><RequireModule module="grantmaker"><BoardPage /></RequireModule></Suspense> },
      { path: 'disbursements', element: <Suspense fallback={<RouteFallback />}><RequireModule module="grantmaker"><DisbursementsPage /></RequireModule></Suspense> },
      { path: 'evidence', element: <Suspense fallback={<RouteFallback />}><RequireModule module="grantmaker"><EvidencePage /></RequireModule></Suspense> },
      { path: 'sed-register', element: <Suspense fallback={<RouteFallback />}><RequireModule module="grantmaker"><SedRegisterPage /></RequireModule></Suspense> },
      { path: 'cases', element: <Suspense fallback={<RouteFallback />}><RequireModule module="grantmaker"><CasesPage /></RequireModule></Suspense> },
      { path: 'calendar', element: <Suspense fallback={<RouteFallback />}><CalendarPage /></Suspense> },
      // The office-bearers register is Compliance, on every plan. The rest
      // of governance, and the board pack, are the governance add-on; with
      // it off the Governance address shows the register and says so.
      { path: 'office-bearers', element: <Suspense fallback={<RouteFallback />}><GovernancePage view="officeBearers" /></Suspense> },
      { path: 'governance', element: <Suspense fallback={<RouteFallback />}><GovernancePage view="governance" /></Suspense> },
      { path: 'board-pack', element: <Suspense fallback={<RouteFallback />}><RequireAddon addon="governance"><BoardPackPage /></RequireAddon></Suspense> },
      { path: 'compliance', element: <Suspense fallback={<RouteFallback />}><CompliancePage /></Suspense> },
      { path: 'funders', element: <Suspense fallback={<RouteFallback />}><RequireModule module="seeker"><PartiesPage /></RequireModule></Suspense> },
      { path: 'tasks', element: <Suspense fallback={<RouteFallback />}><TasksPage /></Suspense> },
      { path: 'answers', element: <Suspense fallback={<RouteFallback />}><RequireAddon addon="answers"><AnswersPage /></RequireAddon></Suspense> },
      { path: 'documents', element: <Suspense fallback={<RouteFallback />}><DocumentsPage /></Suspense> },
      { path: 'settings', element: <Suspense fallback={<RouteFallback />}><SettingsPage /></Suspense> },
      // An unknown address inside a workspace keeps the shell around it.
      { path: '*', element: <NotFoundPage inShell /> },
    ],
  },
  { path: '*', element: <NotFoundPage />, errorElement: <RouteError /> },
]);

// A newer build that took over while a workspace page was open waits for
// the next move rather than reloading under a half-filled form; the move
// then becomes a full load of its destination, on the new build.
let lastPath = window.location.pathname;
router.subscribe((state) => {
  if (state.navigation.state !== 'idle') return;
  const path = state.location.pathname;
  if (path === lastPath) return;
  lastPath = path;
  if (takePendingReload()) window.location.replace(path + state.location.search);
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>,
);
